/**
 * Protected route component that wraps pages inside the application shell and
 * redirects unauthenticated users to the login page.
 *
 * @module routes/ProtectRoute
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import {Route, Redirect} from 'react-router-dom';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import ResponsiveDrawer from '../components/common/ResponsiveDrawer.jsx';

/**
 * Route wrapper that renders a page inside ResponsiveDrawer for authenticated users,
 * or redirects to /login if the user is not authenticated.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ElementType} props.component - The page component to render.
 * @param {boolean} props.isAuth - Whether the current user is authenticated.
 * @param {string} [props.title] - Page title shown in the drawer header.
 * @param {string} [props.subtitle] - Page subtitle shown in the drawer header.
 * @returns {JSX.Element} A protected Route or a Redirect to /login.
 */
const ProtectedRouter = ({
    component: Component,
    isAuth,
    title,
    subtitle,
    ...rest
}) => {
    let redirectUrl = {...rest}.location.pathname + {...rest}.location.search;
    if (window.location.pathname === '/sims/takesim') {
        redirectUrl = '/sims';
    }
    if (window.location.pathname === '/sims/createsim' && !window.location.search) {
        redirectUrl = '/sims';
    }

    localStorage.setItem('redirectUrl', redirectUrl);

    return (isAuth ? (
        <Route
            {...rest}
            render={(props) => (
          <>
                <ResponsiveDrawer
                    title={title}
                    subtitle={subtitle}
                >
                    <Component {...props} />
                </ResponsiveDrawer>
          </>
            )}
        />
    ) : (
        <Redirect to="/login"/>
    ));
};

ProtectedRouter.propTypes = {
    isAuth: PropTypes.bool,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    component: PropTypes.elementType,
};
/**
 * Maps Redux state to the ProtectedRouter component props.
 *
 * @param {Object} state - The Redux store state.
 * @returns {{isAuth: boolean}} Props derived from state.
 */
const mapStateToProps = (state) => ({
    isAuth: state.login.isAuth,
});

export default connect(mapStateToProps, null)(ProtectedRouter);
